/**
 * Shared, dependency-free constants and error-shaping for the Markets
 * section (MarketsPage, StockQuotePage, MoversPage) — chart theming tied to
 * the MD3 dark tokens in dashboard.css, and the `SectionError` shape used to
 * render loading/error/info states consistently across all three pages.
 *
 * Kept separate from marketsShared.tsx (which holds the JSX components that
 * consume these values) because a file mixing component and non-component
 * exports breaks React Fast Refresh (react-refresh/only-export-components).
 */
import { GlorifyApiError } from '../../../services/api/glorify'

// ─── Chart theme — MD3 dark tokens from dashboard.css ──────────────────────

export const md = {
  primary: 'var(--md-sys-color-primary)',
  tertiary: 'var(--md-sys-color-tertiary)',
  error: 'var(--md-sys-color-error)',
  onSurface: 'var(--md-sys-color-on-surface)',
  onSurfaceVariant: 'var(--md-sys-color-on-surface-variant)',
  outlineVariant: 'var(--md-sys-color-outline-variant)',
  surfaceContainerHigh: 'var(--md-sys-color-surface-container-high)',
}

export const CHART_GRID = 'rgba(230, 225, 233, 0.08)'

export const TOOLTIP_STYLE = {
  contentStyle: { background: md.surfaceContainerHigh, border: `1px solid ${md.outlineVariant}`, borderRadius: 8 },
  labelStyle: { color: md.onSurfaceVariant },
  itemStyle: { color: md.onSurface },
}

// ─── Section-level error shaping ────────────────────────────────────────────

export interface SectionError {
  kind: 'info' | 'error'
  message: string
}

/** FMP free-tier/quota gating surfaces as 400 with "FMP" in the message — show as info, not an error. */
export function toSectionError(err: unknown, fallback: string): SectionError {
  if (err instanceof GlorifyApiError) {
    if (err.status === 400 && err.message.includes('FMP')) {
      return { kind: 'info', message: err.message }
    }
    return { kind: 'error', message: err.message }
  }
  return { kind: 'error', message: fallback }
}
